import type { SupabaseClient } from '@supabase/supabase-js';
import type {
    DashboardData, Version, Cycle, Habit, HabitLog, Goal, Profile
} from '@/types/types';
import type { ProfileCacheEntry } from './types';
import { normalizeProblems, calculateDailyScore, calculateHabitStreaks, getLocalDateString } from './shared';
import { getProfile } from './profile';

export async function getDashboardData(
    client: SupabaseClient,
    userId: string,
    getCache: () => ProfileCacheEntry | null,
    setCache: (v: ProfileCacheEntry | null) => void,
): Promise<DashboardData> {
    const profile = await getProfile(client, userId, getCache, setCache);
    if (!profile) throw new Error("Profile not found");
    const resolvedUserId = profile.id || userId;

    const cycleId = profile.current_cycle_id;
    const [currentVersion, currentCycle, allUserHabits, habits, goals] = await Promise.all([
        profile.current_version_id
            ? client.from('versions').select('*').eq('id', profile.current_version_id).single().then(r => r.data as Version | null)
            : null,
        cycleId
            ? client.from('cycles').select('*').eq('id', cycleId).single().then(r => {
                const cycle = r.data as Cycle | null;
                if (cycle) cycle.problems = normalizeProblems(cycle.problems);
                return cycle;
            })
            : null,
        client.from('habits').select('*').eq('user_id', resolvedUserId).then(r => r.data || []) as Promise<Habit[]>,
        cycleId
            ? client.from('habits').select('*').eq('cycle_id', cycleId).order('sort_order').then(r => (r.data || []) as Habit[])
            : [] as Habit[],
        cycleId
            ? client.from('goals').select('*').eq('cycle_id', cycleId).order('sort_order').then(r => (r.data || []) as Goal[])
            : [] as Goal[],
    ]);

    const logs = allUserHabits.length > 0
        ? await client.from('habit_logs').select('*').in('habit_id', allUserHabits.map(h => h.id)).then(r => (r.data || []) as HabitLog[])
        : [] as HabitLog[];

    const todayStr = getLocalDateString();
    const currentHabitIds = new Set(habits.map(h => h.id));
    const todayLogs = logs.filter(l => l.date === todayStr && currentHabitIds.has(l.habit_id));

    const nameToIds = new Map<string, Set<string>>();
    allUserHabits.forEach(ah => {
        if (!nameToIds.has(ah.name)) nameToIds.set(ah.name, new Set());
        nameToIds.get(ah.name)!.add(ah.id);
    });

    const habitStats: Record<string, { currentStreak: number; maxStreak: number; consistency: number }> = {};
    habits.forEach(h => {
        const ids = nameToIds.get(h.name) || new Set();
        const crossCycleLogs = logs.filter(l => ids.has(l.habit_id));
        const normalizedLogs = crossCycleLogs.map(l => ({ ...l, habit_id: h.id }));
        habitStats[h.id] = calculateHabitStreaks(h.id, normalizedLogs);
    });

    // Most-recent-ended-cycle fallback (read-only, user-scoped): lets the
    // honest-dashboard gap banner label the cycle a user just finished when
    // there is no active cycle (or the current cycle failed to resolve).
    // version_number is required (not just sprint_number) — sprint_number is
    // only unique within its own version (see the DOMAIN INVARIANT note in
    // src/lib/services/cycles.ts), so a bare sprint_number here would build
    // an ambiguous/wrong report link if the most-recently-ended cycle across
    // ALL of the user's versions doesn't belong to the currently active one.
    let lastEndedCycle: { id: string; sprint_number: number; version_number: number; hasDebrief: boolean } | null = null;
    if (!cycleId || !currentCycle) {
        const { data: endedRows } = await client
            .from('cycles')
            .select('id, sprint_number, debrief, versions!inner(number)')
            .eq('user_id', resolvedUserId)
            .lt('end_date', todayStr)
            .order('end_date', { ascending: false })
            .limit(1);
        const endedRow = endedRows?.[0] as Record<string, unknown> | undefined;
        if (endedRow) {
            lastEndedCycle = {
                id: endedRow.id as string,
                sprint_number: endedRow.sprint_number as number,
                version_number: (endedRow.versions as { number: number }).number,
                hasDebrief: Boolean(endedRow.debrief),
            };
        }
    }

    return {
        version: currentVersion,
        cycle: currentCycle,
        habits,
        todayLogs,
        habitStats,
        goals,
        lastEndedCycle,
    };
}

export async function getGoalsPageData(
    client: SupabaseClient,
    userId: string,
    getCache: () => ProfileCacheEntry | null,
    setCache: (v: ProfileCacheEntry | null) => void,
): Promise<{
    version: Version | null;
    currentCycle: Cycle | null;
    currentCycleGoals: Goal[];
    pastCycleGoals: Goal[];
    pastCycles: Cycle[];
    currentCycleHabits: Habit[];
    allHabits: Habit[];
    habitStats: Record<string, { currentStreak: number; maxStreak: number; consistency: number }>;
    habitLogs: HabitLog[];
}> {
    const profile = await getProfile(client, userId, getCache, setCache);
    const resolvedUserId = profile?.id || userId;

    const [versionResult, { data: cyclesData }, { data: goals }, { data: habits }, { data: logs }] = await Promise.all([
        profile?.current_version_id
            ? client.from('versions').select('*').eq('id', profile.current_version_id).single().then(r => r.data as Version | null)
            : null,
        client.from('cycles').select('*').eq('user_id', resolvedUserId).order('sprint_number', { ascending: false }),
        client.from('goals').select('*').eq('user_id', resolvedUserId).order('sort_order'),
        client.from('habits').select('*').eq('user_id', resolvedUserId).order('sort_order'),
        client.from('habit_logs').select('*').eq('user_id', resolvedUserId),
    ]);

    const currentVersion = versionResult;
    const allCycles = (cyclesData || []).map(c => ({ ...c, problems: normalizeProblems(c.problems) }));
    const versionCycles = currentVersion ? allCycles.filter(c => c.version_id === currentVersion!.id) : allCycles;

    const currentCycle = versionCycles.find(c => c.id === profile?.current_cycle_id) || null;
    const pastCycles = versionCycles.filter(c => c.id !== profile?.current_cycle_id);

    const allGoals = goals || [];
    const allHabits = habits || [];

    const currentCycleGoals = currentCycle ? allGoals.filter(g => g.cycle_id === currentCycle.id) : [];
    const pastCycleIds = new Set(pastCycles.map(c => c.id));
    const pastCycleGoals = allGoals.filter(g => pastCycleIds.has(g.cycle_id));
    const currentCycleHabits = currentCycle ? allHabits.filter(h => h.cycle_id === currentCycle.id) : [];

    const habitStats: Record<string, { currentStreak: number; maxStreak: number; consistency: number }> = {};
    if (logs) {
        const logsByHabit = new Map<string, HabitLog[]>();
        logs.forEach(l => {
            if (!logsByHabit.has(l.habit_id)) logsByHabit.set(l.habit_id, []);
            logsByHabit.get(l.habit_id)!.push(l);
        });
        allHabits.forEach(h => {
            habitStats[h.id] = calculateHabitStreaks(h.id, logsByHabit.get(h.id) || []);
        });
    }

    return {
        version: currentVersion,
        currentCycle,
        currentCycleGoals,
        pastCycleGoals,
        pastCycles,
        currentCycleHabits,
        allHabits,
        habitStats,
        habitLogs: logs || [],
    };
}

export async function getLabData(
    client: SupabaseClient,
    userId: string,
    getCache: () => ProfileCacheEntry | null,
    setCache: (v: ProfileCacheEntry | null) => void,
    versionId?: string,
): Promise<{
    goals: Goal[];
    version: Version | null;
    cycles: Cycle[];
    habits: Habit[];
    activeCycleId: string | null;
    habitLogs: HabitLog[];
}> {
    const profile = await getProfile(client, userId, getCache, setCache);
    const resolvedUserId = profile?.id || userId;
    const targetVersionId = versionId || profile?.current_version_id;

    const [{ data: versions }, { data: cycles }, { data: goals }, { data: habits }, { data: logs }] = await Promise.all([
        client.from('versions').select('*').eq('user_id', resolvedUserId),
        client.from('cycles').select('*').eq('user_id', resolvedUserId).order('sprint_number', { ascending: false }),
        client.from('goals').select('*').eq('user_id', resolvedUserId).order('sort_order'),
        client.from('habits').select('*').eq('user_id', resolvedUserId).order('sort_order'),
        client.from('habit_logs').select('*').eq('user_id', resolvedUserId),
    ]);

    const selectedVersion = versions?.find((v: Version) => v.id === targetVersionId) || null;
    const normalizedCycles = (cycles || []).map(c => ({ ...c, problems: normalizeProblems(c.problems) }));
    const versionCycles = selectedVersion
        ? normalizedCycles.filter(c => c.version_id === selectedVersion.id)
        : [];

    return {
        goals: goals || [],
        version: selectedVersion,
        cycles: versionCycles,
        habits: habits || [],
        activeCycleId: profile?.current_cycle_id || null,
        habitLogs: logs || [],
    };
}

export async function getHistoryData(
    client: SupabaseClient,
    userId: string,
    days: number = 90,
): Promise<{
    dailyScores: { date: string; score: number; details: { habit: Habit; done: boolean }[] }[];
    cycles: Cycle[];
    habitStats: Record<string, { currentStreak: number; maxStreak: number; consistency: number }>;
}> {
    const today = new Date();
    const startDate = new Date();
    startDate.setDate(today.getDate() - days);
    const startDateStr = startDate.toISOString().split('T')[0];

    const [{ data: logsData }, { data: habitsData }, { data: cyclesData }] = await Promise.all([
        client.from('habit_logs').select('*').eq('user_id', userId).gte('date', startDateStr),
        client.from('habits').select('*').eq('user_id', userId).order('sort_order'),
        client.from('cycles').select('*').eq('user_id', userId),
    ]);
    const logs = logsData || [];
    const habits = habitsData || [];
    const cycles = (cyclesData || []).map(c => ({ ...c, problems: normalizeProblems(c.problems) }));

    const habitStats: Record<string, { currentStreak: number; maxStreak: number; consistency: number }> = {};
    habits.forEach(h => {
        habitStats[h.id] = calculateHabitStreaks(h.id, logs);
    });

    const logsByDate = new Map<string, HabitLog[]>();
    logs.forEach(l => {
        if (!logsByDate.has(l.date)) logsByDate.set(l.date, []);
        logsByDate.get(l.date)!.push(l);
    });
    const habitsByCycle = new Map<string, Habit[]>();
    habits.forEach(h => {
        if (!habitsByCycle.has(h.cycle_id)) habitsByCycle.set(h.cycle_id, []);
        habitsByCycle.get(h.cycle_id)!.push(h);
    });

    const dailyScores = [];
    const todayStr = getLocalDateString();

    for (let i = 0; i <= days; i++) {
        const d = new Date(startDate);
        d.setDate(d.getDate() + i);
        const dateStr = getLocalDateString(d);
        if (dateStr > todayStr) break;

        const activeCycle = cycles.find(c => c.start_date <= dateStr && c.end_date >= dateStr);
        const daysHabits = activeCycle ? (habitsByCycle.get(activeCycle.id) || []) : [];
        const dayLogs = logsByDate.get(dateStr) || [];

        let score = 0;
        const details: { habit: Habit; done: boolean }[] = [];

        if (daysHabits.length > 0) {
            score = calculateDailyScore(daysHabits, dayLogs);
            const doneLogs = new Set(dayLogs.filter(l => l.status).map(l => l.habit_id));
            daysHabits.forEach(h => {
                details.push({ habit: h, done: doneLogs.has(h.id) });
            });
        }

        dailyScores.push({ date: dateStr, score, details });
    }

    return { dailyScores, cycles, habitStats };
}
